import { useEffect, useRef, useState } from 'react';
import { Alert, Button, TextField } from '@mui/material';
import type { RelevanceDecision } from '../../../api/src/fakes/fakes.types';
import type { FakesResult } from '../api/types';
import { api } from '../api/client';
import { EditorOnly, useCanEdit } from './EditorOnly';
import { Stack, Typography } from './mui-system';
import { fakeDate, fakeError } from './fakes-presentation';

export function SerpRelevance({
  workspaceId,
  result,
  onSaved,
  onClose,
}: {
  workspaceId: string;
  result: FakesResult;
  onSaved(): Promise<void>;
  onClose(): void;
}) {
  const canEdit = useCanEdit('fakes.edit');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState<RelevanceDecision[] | null>(null);
  const live = useRef(true),
    locked = useRef(false);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  async function save() {
    if (!canEdit || locked.current || !reason.trim()) return;
    locked.current = true;
    setBusy(true);
    setError('');
    try {
      await api.decideRelevance(workspaceId, result.id, {
        noise: !result.relevance?.noise,
        reason,
        expectedId: result.relevance?.id ?? null,
      });
      await onSaved();
      if (live.current) onClose();
    } catch (e) {
      if (live.current) setError(fakeError(e));
    } finally {
      locked.current = false;
      if (live.current) setBusy(false);
    }
  }
  async function loadHistory() {
    setError('');
    try {
      const rows = await api.relevanceHistory(workspaceId, result.id);
      if (live.current) setHistory(rows);
    } catch (e) {
      if (live.current) setError(fakeError(e));
    }
  }
  return (
    <Stack gap={1.5} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography fontWeight={700}>Релевантность запросу</Typography>
      <Typography fontSize={13}>
        {result.relevance?.noise
          ? 'Шум · нерелевантный результат'
          : result.relevance
            ? 'Метка «Шум» снята'
            : 'Релевантность не оценена'}
      </Typography>
      <Typography fontSize={12} color="text.secondary">
        Метка относится только к этому URL в этом запросе. Она не меняет принадлежность ресурса или
        решение о фейке.
      </Typography>
      {result.relevance && (
        <Typography fontSize={12}>
          {result.relevance.reason} · {fakeDate(result.relevance.createdAt)}
        </Typography>
      )}
      <EditorOnly permissions="fakes.edit">
        <TextField
          label="Основание метки"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 2000 } }}
        />
        <Button variant="outlined" disabled={busy || !reason.trim()} onClick={() => void save()}>
          {result.relevance?.noise ? 'Снять метку «Шум»' : 'Пометить как шум'}
        </Button>
      </EditorOnly>
      {error && <Alert severity="error">{error}</Alert>}
      <Button size="small" onClick={() => void loadHistory()}>
        История релевантности
      </Button>
      {history?.length === 0 && <Typography fontSize={12}>Оценок пока нет.</Typography>}
      {history?.map((r) => (
        <Stack key={r.id}>
          <Typography fontSize={12}>
            {r.noise ? 'Шум' : 'Метка снята'} · {fakeDate(r.createdAt)}
          </Typography>
          <Typography fontSize={12}>{r.reason}</Typography>
          <Typography fontSize={11} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {r.actorId}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
